import clsx from 'clsx';
import type * as React from 'react';

import { type BlockImagesAlign, type BlockImagesData, images } from '@gitbook/doc-core';
import { type PaletteCommandDef, PaletteCommandType } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { useInsertFiles } from '../../InsertFilesDialog';
import { BlockWrapper, useBlockLayoutGroupFactory, useBlockPaletteFactory } from '../common';

/**
 * Renderer for an images block.
 */
export function BlockImages(props: BlockProps): React.ReactElement {
    const { node, editor, children } = props;
    const { align = 'center' } = node.data.toFrozenObject<BlockImagesData>();

    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey: node.key });
    const insertFiles = useInsertFiles();

    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () => {
            const block = editor.value.document.assertBlock(node.key);
            const data = block.data.toFrozenObject<BlockImagesData>();

            /** Update the alignment in the block. */
            const changeAlign = (align: BlockImagesAlign) => {
                editor.change((change) => {
                    change.insertDataByKey(node.key, ['align'], align);
                });
            };

            return [
                {
                    key: 'types',
                    commands: () => {
                        return [
                            {
                                key: 'files',
                                title: 'Add images…',
                                icon: Icon.Image,
                                trigger: () => {
                                    insertFiles
                                        .prompt({
                                            accept: 'images',
                                            title: 'Select image',
                                            acceptURL: true,
                                        })
                                        .then((refs) => {
                                            editor.change((change) => {
                                                refs.forEach((ref) => {
                                                    change.appendNodeByKey(
                                                        node.key,
                                                        images.createImageBlock(ref)
                                                    );
                                                });
                                                change.focus();
                                            });
                                        });
                                },
                            },
                            // The frame option is available for single image blocks
                            block.nodes.size === 1
                                ? ({
                                      key: 'with-frame',
                                      icon: Icon.ImageFrame,
                                      type: PaletteCommandType.SWITCH,
                                      enabled: Boolean(data.withFrame),
                                      title: 'With frame',
                                      trigger: () => {
                                          editor.change((change) => {
                                              if (data.withFrame) {
                                                  change.removeDataPathByKey(node.key, [
                                                      'withFrame',
                                                  ]);
                                              } else {
                                                  change.insertDataByKey(
                                                      node.key,
                                                      ['withFrame'],
                                                      true
                                                  );
                                              }
                                          });
                                      },
                                  } satisfies PaletteCommandDef)
                                : null,
                        ].filter(filterOutNullable);
                    },
                },
                // Commands for alignment
                {
                    key: 'align',
                    commands: [
                        {
                            key: 'left',
                            type: PaletteCommandType.RADIO,
                            title: 'Left align',
                            selected: align === 'left',
                            icon: Icon.AlignTextLeft,
                            trigger: () => changeAlign('left'),
                        } satisfies PaletteCommandDef,
                        {
                            key: 'center',
                            type: PaletteCommandType.RADIO,
                            title: 'Center',
                            selected: align === 'center',
                            icon: Icon.AlignTextCenter,
                            trigger: () => changeAlign('center'),
                        } satisfies PaletteCommandDef,
                        {
                            key: 'right',
                            type: PaletteCommandType.RADIO,
                            title: 'Right align',
                            selected: align === 'right',
                            icon: Icon.AlignTextRight,
                            trigger: () => changeAlign('right'),
                        } satisfies PaletteCommandDef,
                    ],
                },
                createLayoutGroup(),
            ].filter(filterOutNullable);
        },
    });

    return (
        <BlockWrapper palette={palette} {...props}>
            <div
                className={clsx(
                    'flex gap-3 max-md:flex-wrap max-md:justify-center',
                    (
                        {
                            center: 'justify-center',
                            left: 'justify-start',
                            right: 'justify-end',
                        } satisfies Record<BlockImagesAlign, string>
                    )[align]
                )}
            >
                {children}
            </div>
        </BlockWrapper>
    );
}
